import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Flex,
  Spinner,
  Alert,
  AlertIcon,
  Text,
  Badge,
  ButtonGroup,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  useDisclosure,
  Checkbox,
  Select,
} from '@chakra-ui/react';
import { FiPlus, FiEdit2, FiExternalLink } from 'react-icons/fi';
import { serviceApi } from '../services/api';

const CATEGORY_OPTIONS = ['Injectables', 'Lasers', 'Skincare', 'Consultation', 'Body Treatments'];

function ServicesManagement() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [serviceForm, setServiceForm] = useState({
    name: '',
    description: '',
    category: '',
    price: '',
    duration_minutes: '',
    is_active: true,
  });
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();

  useEffect(() => {
    fetchServices();
    // eslint-disable-next-line
  }, [showInactive]);

  const fetchServices = async () => {
    try {
      setLoading(true);
      const response = await serviceApi.getAll(!showInactive);
      setServices(response.data);
      setError(null);
    } catch (err) {
      setError('Failed to fetch services');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (service = null) => {
    if (service) {
      setEditingService(service);
      setServiceForm({
        name: service.name,
        description: service.description || '',
        category: service.category || '',
        price: service.price,
        duration_minutes: service.duration_minutes || '',
        is_active: service.is_active,
      });
    } else {
      setEditingService(null);
      setServiceForm({
        name: '',
        description: '',
        category: '',
        price: '',
        duration_minutes: '',
        is_active: true,
      });
    }
    onOpen();
  };

  const handleCloseModal = () => {
    setEditingService(null);
    setServiceForm({
      name: '',
      description: '',
      category: '',
      price: '',
      duration_minutes: '',
      is_active: true,
    });
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingService) {
        await serviceApi.update(editingService.id, serviceForm);
        toast({
          title: 'Service updated successfully',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } else {
        await serviceApi.create(serviceForm);
        toast({
          title: 'Service created successfully',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }
      handleCloseModal();
      fetchServices();
    } catch (err) {
      toast({
        title: editingService ? 'Failed to update service' : 'Failed to create service',
        description: err.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      console.error(err);
    }
  };

  const handleToggleActive = async (service) => {
    try {
      if (service.is_active) {
        await serviceApi.delete(service.id);
        toast({
          title: 'Service deactivated',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } else {
        await serviceApi.update(service.id, { ...service, is_active: true });
        toast({
          title: 'Service reactivated',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }
      fetchServices();
    } catch (err) {
      toast({
        title: 'Failed to update service status',
        description: err.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      console.error(err);
    }
  };

  if (loading) {
    return (
      <Flex justify="center" align="center" h="200px">
        <Spinner size="xl" color="brand.purple" thickness="4px" />
      </Flex>
    );
  }

  if (error) {
    return (
      <Alert status="error" borderRadius="lg">
        <AlertIcon />
        {error}
      </Alert>
    );
  }

  // Group services by category
  const servicesByCategory = services.reduce((acc, service) => {
    const category = service.category || 'Uncategorized';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(service);
    return acc;
  }, {});

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={6} flexWrap="wrap" gap={4}>
        <Box>
          <Heading size="md" mb={2}>Services Management</Heading>
          <Text color="gray.600" fontSize="sm">
            Quick access to manage treatment services and pricing
          </Text>
        </Box>
        <Flex gap={4} align="center">
          <Button
            as={RouterLink}
            to="/app/services"
            leftIcon={<FiExternalLink />}
            variant="outline"
            colorScheme="purple"
            size="sm"
          >
            Full Services View
          </Button>
          <Checkbox
            isChecked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
            colorScheme="purple"
          >
            Show Inactive
          </Checkbox>
          <Button
            leftIcon={<FiPlus />}
            colorScheme="purple"
            onClick={() => handleOpenModal()}
          >
            Add Service
          </Button>
        </Flex>
      </Flex>

      {services.length === 0 ? (
        <Text color="gray.500">No services found.</Text>
      ) : (
        <Box overflowX="auto" bg="white" borderRadius="xl" boxShadow="sm">
          <Table variant="simple" size="sm">
            <Thead bg="brand.darkPurple">
              <Tr>
                <Th color="white">Service Name</Th>
                <Th color="white">Category</Th>
                <Th color="white">Price</Th>
                <Th color="white">Status</Th>
                <Th color="white">Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {Object.entries(servicesByCategory).map(([category, categoryServices]) => (
                <React.Fragment key={category}>
                  <Tr bg="gray.50">
                    <Td colSpan={5} fontWeight="bold" fontSize="sm" color="purple.700">
                      {category}
                    </Td>
                  </Tr>
                  {categoryServices.map((service) => (
                    <Tr
                      key={service.id}
                      opacity={service.is_active ? 1 : 0.5}
                      _hover={{ bg: 'brand.lightGray' }}
                    >
                      <Td fontWeight="medium">{service.name}</Td>
                      <Td>
                        {service.category ? (
                          <Badge colorScheme="purple" variant="subtle" size="sm">
                            {service.category}
                          </Badge>
                        ) : (
                          <Text color="gray.400" fontSize="xs">-</Text>
                        )}
                      </Td>
                      <Td>${parseFloat(service.price).toFixed(2)}</Td>
                      <Td>
                        <Badge colorScheme={service.is_active ? 'green' : 'red'} size="sm">
                          {service.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </Td>
                      <Td>
                        <ButtonGroup size="xs" spacing={2}>
                          <Button
                            leftIcon={<FiEdit2 />}
                            colorScheme="purple"
                            variant="outline"
                            onClick={() => handleOpenModal(service)}
                          >
                            Edit
                          </Button>
                          <Button
                            colorScheme={service.is_active ? 'red' : 'green'}
                            variant="outline"
                            onClick={() => handleToggleActive(service)}
                          >
                            {service.is_active ? 'Deactivate' : 'Reactivate'}
                          </Button>
                        </ButtonGroup>
                      </Td>
                    </Tr>
                  ))}
                </React.Fragment>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}

      {/* Service Form Modal */}
      <Modal isOpen={isOpen} onClose={handleCloseModal} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {editingService ? 'Edit Service' : 'Add New Service'}
          </ModalHeader>
          <ModalCloseButton />
          <form onSubmit={handleSubmit}>
            <ModalBody>
              <FormControl isRequired mb={4}>
                <FormLabel>Service Name</FormLabel>
                <Input
                  value={serviceForm.name}
                  onChange={(e) => setServiceForm({ ...serviceForm, name: e.target.value })}
                  placeholder="e.g., Botox, Dermal Filler"
                />
              </FormControl>

              <FormControl mb={4}>
                <FormLabel>Description</FormLabel>
                <Textarea
                  value={serviceForm.description}
                  onChange={(e) => setServiceForm({ ...serviceForm, description: e.target.value })}
                  rows={2}
                  placeholder="Brief description"
                />
              </FormControl>

              <FormControl mb={4}>
                <FormLabel>Category</FormLabel>
                <Select
                  value={serviceForm.category}
                  onChange={(e) => setServiceForm({ ...serviceForm, category: e.target.value })}
                  placeholder="Select category"
                >
                  {CATEGORY_OPTIONS.map((cat) => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </Select>
              </FormControl>

              <FormControl isRequired mb={4}>
                <FormLabel>Price ($)</FormLabel>
                <NumberInput
                  min={0}
                  precision={2}
                  value={serviceForm.price}
                  onChange={(valueString) =>
                    setServiceForm({ ...serviceForm, price: valueString })
                  }
                >
                  <NumberInputField placeholder="0.00" />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>

              <FormControl mb={4}>
                <FormLabel>Duration (minutes)</FormLabel>
                <NumberInput
                  min={0}
                  value={serviceForm.duration_minutes}
                  onChange={(valueString) =>
                    setServiceForm({ ...serviceForm, duration_minutes: valueString })
                  }
                >
                  <NumberInputField placeholder="e.g., 30" />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>

              <FormControl>
                <Checkbox
                  isChecked={serviceForm.is_active}
                  onChange={(e) =>
                    setServiceForm({ ...serviceForm, is_active: e.target.checked })
                  }
                  colorScheme="purple"
                >
                  Active
                </Checkbox>
              </FormControl>
            </ModalBody>
            <ModalFooter>
              <ButtonGroup spacing={3}>
                <Button variant="ghost" onClick={handleCloseModal}>
                  Cancel
                </Button>
                <Button colorScheme="purple" type="submit">
                  {editingService ? 'Update' : 'Create'}
                </Button>
              </ButtonGroup>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>
    </Box>
  );
}

export default ServicesManagement;
